import { useState, useEffect, useRef } from "react";
import FooterView from "../../components/footerView";

// ─── Font note ───────────────────────────────────────────────────────────────
// Add to index.html <head>:
// <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap" rel="stylesheet">
// Add to tailwind.config.js theme.extend.fontFamily:
// serif: ['"Instrument Serif"', 'Georgia', 'serif'],
// sans:  ['"DM Sans"', 'sans-serif'],
// ─────────────────────────────────────────────────────────────────────────────

// ─── DATA ────────────────────────────────────────────────────────────────────
const CATEGORIES = [
    {
        id: "core",
        label: "Core SEO",
        services: [
            {
                slug: "on_site",
                name: "On-Page SEO Optimization",
                tagline: "Maximize your most important pages",
                price: "$950",
                priceNote: "one-time",
                href: "/services/on-page-seo",
                accent: "amber",
                description:
                    "A deep optimization of 3 key pages — homepage, category, or flagship product. We rewrite title tags, meta, headers, and body copy to convert and rank.",
                tags: ["On-page", "Copy", "Technical"],
            },
            {
                slug: "technical_seo",
                name: "Technical SEO Health Audit",
                tagline: "Fix what's silently killing your rankings",
                price: "$150",
                priceNote: "one-time",
                href: "/services/technical-seo-audit",
                accent: "violet",
                description:
                    "A comprehensive crawl of your site's technical foundation — indexation, speed, mobile, and more — with a clear action plan delivered in 7 days.",
                tags: ["Audit", "Technical", "Indexing"],
            },
            {
                slug: "site_speed",
                name: "Site Speed Optimization",
                tagline: "Faster sites rank higher and convert more",
                price: "$2,000",
                priceNote: "one-time",
                href: "/services/site-speed",
                accent: "teal",
                description:
                    "Hands-on Core Web Vitals optimization (LCP, INP, CLS) for your 5 most important pages. A faster site is an SEO advantage and a revenue advantage.",
                tags: ["Speed", "Core Web Vitals", "UX"],
            },
            {
                slug: "schema",
                name: "Schema Markup Implementation",
                tagline: "Earn rich results and more SERP real estate",
                price: "$1,200",
                priceNote: "one-time",
                href: "/services/schema-markup",
                accent: "amber",
                description:
                    "Structured data added to product, FAQ, and blog pages so Google shows stars, prices, and rich snippets — taking up more space and earning more clicks.",
                tags: ["Structured Data", "SERP", "E-commerce"],
            },
            {
                slug: "keyword_analysis",
                name: "Keyword Gap Analysis",
                tagline: "Find the keywords your competitors are hiding",
                price: "$900",
                priceNote: "one-time",
                href: "/services/keyword-gap",
                accent: "violet",
                description:
                    "A report identifying the high-value keywords your competitors rank for — but you don't. Perfect for brands stuck in a content rut or looking for new opportunities.",
                tags: ["Research", "Competitors", "Strategy"],
            },
            {
                slug: "competitor_analysis",
                name: "SEO Competitor Analysis",
                tagline: "Reverse-engineer what's working for them",
                price: "$1,500",
                priceNote: "one-time",
                href: "/services/competitor-analysis",
                accent: "teal",
                description:
                    "A deep dive into the SEO strategies of your top 3 competitors — their content, links, and technical setup — so you can out-maneuver them.",
                tags: ["Competitors", "Strategy", "Research"],
            },
            {
                slug: "content_writing",
                name: "SEO-Optimized Content Writing",
                tagline: "Content that ranks, attracts links, and converts",
                price: "$850",
                priceNote: "one-time",
                href: "/services/content-writing",
                accent: "amber",
                description:
                    "10 professionally written blog posts, buying guides, or pillar pages built for topical authority and long-tail traffic. Includes keyword research and internal linking.",
                tags: ["Content", "Blog", "Authority"],
            },
            {
                slug: "link_building",
                name: "Link Building Outreach",
                tagline: "Earn the backlinks that move rankings",
                price: "$1,200",
                priceNote: "one-time",
                href: "/services/link-building",
                accent: "violet",
                description:
                    "5 targeted outreach campaigns to earn high-quality dofollow backlinks from relevant websites. Ideal for new sites and those with low domain authority.",
                tags: ["Backlinks", "Outreach", "Authority"],
            },
        ],
    },
    {
        id: "local",
        label: "Local & E-commerce",
        services: [
            {
                slug: "local_seo",
                name: "Local SEO Setup & Optimization",
                tagline: "Dominate your city on Google Maps",
                price: "$997",
                priceNote: "one-time",
                href: "/services/local-seo",
                accent: "teal",
                description:
                    "Complete Google Business Profile optimization plus key local citations on Apple Maps, Bing, and Yelp. Built for brick-and-mortar and service-area businesses.",
                tags: ["Local", "Google Maps", "Citations"],
            },
            {
                slug: "ecommerce",
                name: "E-commerce Product Page Optimization",
                tagline: "Turn product pages into ranking machines",
                price: "$1,200",
                priceNote: "one-time",
                href: "/services/ecommerce-seo",
                accent: "amber",
                description:
                    "Optimization of 15 product pages — title tags, meta descriptions, image ALT text, internal linking, and schema — so they rank and convert.",
                tags: ["E-commerce", "Product Pages", "Conversion"],
            },
        ],
    },
    {
        id: "emerging",
        label: "Emerging & Premium",
        services: [
            {
                slug: "geo",
                name: "AI SEO — Generative Engine Optimization",
                tagline: "Be the answer AI gives about your industry",
                price: "$1,500",
                priceNote: "one-time",
                href: "/services/geo",
                accent: "violet",
                featured: true,
                description:
                    "We optimize your content to be selected, quoted, and cited by ChatGPT, Perplexity, and Google AI Overviews — the fastest-growing search channels of 2025.",
                tags: ["AI Search", "GEO", "New"],
            },
            {
                slug: "gkp",
                name: "Google Knowledge Panel",
                tagline: "Own your official presence on Google Search",
                price: "$1,850",
                priceNote: "one-time",
                href: "/services/knowledge-panel",
                accent: "amber",
                description:
                    "Your brand's official sidebar on Google Search — displaying key information instantly and building immediate trust with every searcher. We handle the entire process.",
                tags: ["Brand", "Authority", "Google"],
            },
        ],
    },
    {
        id: "retainers",
        label: "Monthly Retainers",
        services: [
            {
                slug: "foundation",
                name: "Foundation Retainer",
                tagline: "Stop being invisible on Google",
                price: "$997",
                priceNote: "/ mo",
                href: "/pricing",
                accent: "teal",
                description:
                    "Technical audit + fixes, up to 5 pages optimized, Google Business Profile setup, and monthly rankings report. For local businesses and early-stage brands.",
                tags: ["Retainer", "Local", "Foundation"],
            },
            {
                slug: "growth",
                name: "Growth Retainer",
                tagline: "Build authority and scale qualified traffic",
                price: "$1,997",
                priceNote: "/ mo",
                href: "/pricing",
                accent: "violet",
                featured: true,
                description:
                    "Everything in Foundation plus 15–20 pages optimized, 2 SEO blog posts written and published monthly, 2–4 backlinks earned, and competitor gap analysis.",
                tags: ["Retainer", "Content", "Links"],
            },
            {
                slug: "authority",
                name: "Authority Retainer",
                tagline: "Compete at the top of your market",
                price: "$3,800",
                priceNote: "/ mo",
                href: "/pricing",
                accent: "amber",
                description:
                    "30–50 pages optimized, 4–8 content pieces per month, 5–10 high-authority links, digital PR outreach, and a quarterly strategy workshop.",
                tags: ["Retainer", "Enterprise", "PR"],
            },
            {
                slug: "market-leader",
                name: "Market Leader Retainer",
                tagline: "A fully bespoke partnership",
                price: "$7,500",
                priceNote: "/ mo",
                href: "/pricing",
                accent: "teal",
                description:
                    "Enterprise technical SEO, Google Knowledge Panel, GEO optimization, multi-location and international SEO, with priority phone support and 1:1 onboarding.",
                tags: ["Retainer", "Enterprise", "Bespoke"],
            },
        ],
    },
];

const ACCENT_STYLES = {
    amber: {
        tag: "bg-amber-50 text-amber-800",
        badge: "bg-amber-100 text-amber-900",
        border: "group-hover:border-amber-300",
        dot: "bg-amber-400",
        price: "text-amber-800",
    },
    violet: {
        tag: "bg-violet-50 text-violet-800",
        badge: "bg-violet-100 text-violet-900",
        border: "group-hover:border-violet-300",
        dot: "bg-violet-400",
        price: "text-violet-800",
    },
    teal: {
        tag: "bg-teal-50 text-teal-800",
        badge: "bg-teal-100 text-teal-900",
        border: "group-hover:border-teal-300",
        dot: "bg-teal-400",
        price: "text-teal-800",
    },
};

// ─── HOOKS ───────────────────────────────────────────────────────────────────
function useInView() {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(el); } },
            { threshold: 0.1 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return [ref, inView];
}

// ─── COMPONENTS ──────────────────────────────────────────────────────────────
function Reveal({ children, delay = 0, className = "" }) {
    const [ref, inView] = useInView();
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
            }}
        >
            {children}
        </div>
    );
}

function Nav({ activeCategory, onCategoryClick }) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", fn);
        return () => window.removeEventListener("scroll", fn);
    }, []);

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-[#faf9f6]/95 backdrop-blur-md border-b border-black/8 shadow-sm" : "bg-transparent"
            }`}
        >
            <div className="max-w-6xl mx-auto px-6 flex items-center justify-between py-4">
                <a href="/" className="font-serif text-lg text-stone-900 no-underline">
                    Her<span className="text-amber-700">Story</span> SEO
                </a>
                <div className="hidden md:flex items-center gap-1">
                    {CATEGORIES.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => onCategoryClick(cat.id)}
                            className={`text-xs px-3 py-1.5 rounded-full transition-all duration-200 ${
                                activeCategory === cat.id
                                    ? "bg-stone-900 text-white"
                                    : "text-stone-500 hover:text-stone-900"
                            }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>
                <a
                    href="/seo-retainer"
                    className="text-xs font-medium bg-stone-900 text-white px-4 py-2 rounded-full no-underline hover:bg-amber-800 transition-colors duration-200"
                >
                    View retainers
                </a>
            </div>
        </nav>
    );
}

function HeroSection() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);
    const fade = (d) => ({
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.85s ease ${d}ms, transform 0.85s ease ${d}ms`,
    });

    const totalServices = CATEGORIES.reduce((acc, c) => acc + c.services.length, 0);

    return (
        <section className="relative pt-32 pb-16 px-6 overflow-hidden">
            {/* Soft background radials */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-0 w-[500px] h-[400px] bg-amber-100/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-violet-100/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            </div>

            <div className="relative max-w-6xl mx-auto">
                <div style={fade(80)} className="inline-flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-stone-400 mb-6">
                    <span className="w-4 h-px bg-stone-300 inline-block" />
                    All Services
                    <span className="w-4 h-px bg-stone-300 inline-block" />
                </div>
                <h1 style={fade(180)} className="font-serif text-[clamp(40px,6vw,80px)] leading-[1.06] text-stone-900 max-w-3xl mb-6">
                    Every service we offer,<br />
                    <em className="italic text-amber-800">in one place.</em>
                </h1>
                <p style={fade(300)} className="text-base text-stone-500 max-w-xl leading-relaxed font-light mb-10">
                    From a one-time technical audit to a full-service retainer — browse all {totalServices} services across {CATEGORIES.length} categories. Click any card to learn more and get started.
                </p>

                {/* Stats row */}
                <div style={fade(420)} className="flex gap-8 flex-wrap">
                    {[
                        { n: totalServices + "+", label: "Services available" },
                        { n: "4", label: "Service categories" },
                        { n: "$750", label: "Starting from" },
                        { n: "30 days", label: "Avg. delivery time" },
                    ].map(({ n, label }) => (
                        <div key={label}>
                            <div className="font-serif text-2xl text-stone-800">{n}</div>
                            <div className="text-xs text-stone-400 mt-0.5">{label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ServiceCard({ service, index }) {
    const a = ACCENT_STYLES[service.accent];
    return (
        <Reveal delay={index * 60}>
            <a
                // href={service.href}
                href={"/shop"}
                className={`group relative flex flex-col bg-white rounded-2xl border border-black/8 p-6 no-underline transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${a.border} h-full`}
            >
                {service.featured && (
                    <div className="absolute -top-2.5 left-5">
            <span className={`text-[10px] font-medium tracking-widest uppercase px-2.5 py-1 rounded-full ${a.badge}`}>
              Popular
            </span>
                    </div>
                )}

                {/* Price pill */}
                <div className="flex items-start justify-between mb-4">
                    <div className={`text-xs font-medium ${a.price}`}>
                        <span className="text-xl font-semibold">{service.price}</span>
                        <span className="text-xs font-normal text-stone-400 ml-1">{service.priceNote}</span>
                    </div>
                    <div className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center transition-all duration-200 group-hover:border-black/30 group-hover:bg-stone-50">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="text-stone-400 group-hover:text-stone-700 transition-colors" />
                        </svg>
                    </div>
                </div>

                <h3 className="font-serif text-[17px] leading-snug text-stone-900 mb-1 group-hover:text-stone-700 transition-colors">
                    {service.name}
                </h3>
                <p className="text-xs text-stone-400 mb-3 font-light italic">{service.tagline}</p>
                <p className="text-sm text-stone-500 leading-relaxed font-light flex-1">{service.description}</p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-black/5">
                    {service.tags.map((tag) => (
                        <span key={tag} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${a.tag}`}>
              {tag}
            </span>
                    ))}
                </div>
            </a>
        </Reveal>
    );
}

function CategorySection({ category, sectionRef }) {
    return (
        <section ref={sectionRef} id={category.id} className="scroll-mt-24">
            <Reveal>
                <div className="flex items-center gap-4 mb-8">
                    <h2 className="font-serif text-[clamp(22px,3vw,32px)] text-stone-900">{category.label}</h2>
                    <div className="flex-1 h-px bg-stone-200" />
                    <span className="text-xs text-stone-400">{category.services.length} services</span>
                </div>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {category.services.map((service, i) => (
                    <ServiceCard key={service.slug} service={service} index={i} />
                ))}
            </div>
        </section>
    );
}

function FilterBar({ active, onChange }) {
    return (
        <div className="sticky top-[65px] z-40 bg-[#faf9f6]/95 backdrop-blur-sm border-b border-black/5 py-3 px-6 mb-10">
            <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
                <button
                    onClick={() => onChange("all")}
                    className={`shrink-0 text-xs px-4 py-1.5 rounded-full border transition-all duration-200 ${
                        active === "all"
                            ? "bg-stone-900 text-white border-stone-900"
                            : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                    }`}
                >
                    All services
                </button>
                {CATEGORIES.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => onChange(cat.id)}
                        className={`shrink-0 text-xs px-4 py-1.5 rounded-full border transition-all duration-200 ${
                            active === cat.id
                                ? "bg-stone-900 text-white border-stone-900"
                                : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                        }`}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function CtaBanner() {
    return (
        <Reveal>
            <div className="relative bg-stone-900 rounded-3xl p-10 md:p-14 text-center overflow-hidden my-16">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-amber-400/10 rounded-full blur-3xl" />
                <p className="text-xs font-medium tracking-widest uppercase text-amber-400 mb-4 relative">Not sure where to start?</p>
                <h3 className="font-serif text-[clamp(28px,4vw,48px)] text-white leading-tight mb-4 relative">
                    Start with the audit.<br />
                    <em className="italic text-amber-400">Everything else follows.</em>
                </h3>
                <p className="text-sm text-white/50 max-w-md mx-auto font-light mb-8 relative">
                    Our Technical SEO Audit gives you a clear picture of what's holding your site back — and a prioritized action plan to fix it. It's the natural first step for any business.
                </p>
                <div className="flex gap-3 justify-center flex-wrap relative">
                    <a
                        href="/shop"
                        className="bg-white text-stone-900 text-sm font-medium px-7 py-3 rounded-full no-underline hover:bg-amber-50 transition-colors duration-200"
                    >
                        Get the audit — $150
                    </a>
                    <a
                        href="/seo-retainer"
                        className="bg-transparent text-white text-sm font-normal px-7 py-3 rounded-full border border-white/20 no-underline hover:border-white/50 transition-colors duration-200"
                    >
                        Compare retainers
                    </a>
                </div>
            </div>
        </Reveal>
    );
}

function Footer() {
    return (
        <FooterView />
    );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────
export default function ServicesHubPage() {
    const [activeCategory, setActiveCategory] = useState("all");
    const sectionRefs = useRef({});

    // Register refs for each category
    CATEGORIES.forEach((cat) => {
        if (!sectionRefs.current[cat.id]) {
            sectionRefs.current[cat.id] = { current: null };
        }
    });

    const scrollToCategory = (id) => {
        setActiveCategory(id);
        if (id === "all") {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        const el = document.getElementById(id);
        if (el) {
            const offset = 120;
            const top = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: "smooth" });
        }
    };

    // Update active pill on scroll
    useEffect(() => {
        const fn = () => {
            const offset = 160;
            let current = "all";
            CATEGORIES.forEach((cat) => {
                const el = document.getElementById(cat.id);
                if (el && el.getBoundingClientRect().top <= offset) {
                    current = cat.id;
                }
            });
            setActiveCategory(current);
        };
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    const visibleCategories =
        activeCategory === "all"
            ? CATEGORIES
            : CATEGORIES.filter((c) => c.id === activeCategory);

    return (
        <div className="font-sans bg-[#faf9f6] text-stone-900 min-h-screen">
            <Nav activeCategory={activeCategory} onCategoryClick={scrollToCategory} />
            <HeroSection />
            <FilterBar active={activeCategory} onChange={scrollToCategory} />

            <main className="max-w-6xl mx-auto px-6 pb-16 flex flex-col gap-16">
                {CATEGORIES.map((cat) => (
                    <CategorySection
                        key={cat.id}
                        category={cat}
                        sectionRef={(el) => {
                            if (el) sectionRefs.current[cat.id] = { current: el };
                        }}
                    />
                ))}
                <CtaBanner />
            </main>

            <Footer />
        </div>
    );
}